import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeftRight, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { confirmSheet } from "@/components/ui/action-sheet";
import { plural } from "@/components/crm/CustomerActions";

function Choice({ f, pick, setPick, i }) {
  const opt = (side) => {
    const on = pick === side;
    const text = f[`${side}_label`] || "—";
    return (
      <button type="button" data-testid={`merge-field-${f.key}-${side}`} onClick={() => setPick(side)}
        className={`min-h-[48px] min-w-0 flex-1 rounded-[10px] border px-3 py-2 text-left text-[15px] transition-colors ${on ? "border-[#007AFF] bg-[#007AFF]/[0.06] text-black" : "border-black/10 bg-white text-[#3C3C43]/80"}`}>
        <span className="block truncate">{text}</span>
      </button>
    );
  };
  return (
    <div data-testid={`merge-field-${i}`} className="space-y-1.5 border-b border-black/[0.08] px-4 py-3 last:border-0">
      <p className="text-[13px] uppercase text-[#6D6D72]">{f.label}{f.primary === f.other ? " · same" : ""}</p>
      <div className="flex gap-2">{opt("primary")}{opt("other")}</div>
    </div>
  );
}

export default function MergeCustomers() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const other = sp.get("with");
  const [pv, setPv] = useState(null);
  const [choose, setChoose] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let live = true;
    api.get(`/customers/${id}/merge-preview`, { params: { other } }).then((r) => {
      if (!live) return;
      setPv(r.data);
      setChoose((cur) => (Object.keys(cur).length ? cur : Object.fromEntries(r.data.fields.map((f) => [f.key, f.default]))));
    }).catch((e) => { if (live) { toast.error(errorMessage(e)); nav(`/customers/${id}`); } });
    return () => { live = false; };
  }, [id, other, nav]);
  if (!pv) return <div className="h-40 animate-pulse rounded-2xl bg-slate-900/30" />;
  const { primary: a, other: b } = pv;
  const run = async () => {
    const moved = `${plural(b.projects, "project")}, ${plural(b.estimates, "estimate")} and ${plural(b.invoices, "invoice")}`;
    if (!(await confirmSheet({ title: `Merge ${b.name} into ${a.name}?`, message: `${moved} move to ${a.name}. ${b.name} goes to Recently deleted for 30 days.`, confirm: "Merge Customers", destructive: true }))) return;
    setBusy(true);
    try { const { data } = await api.post(`/customers/${id}/merge`, { other_id: other, choose }); toast.success(data.message); nav(`/customers/${id}`); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const showroomMismatch = !pv.same_showroom;
  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="merge-page">
      <div className="flex items-center justify-between">
        <Link to={`/customers/${id}`} data-testid="merge-cancel" className="inline-flex min-h-[44px] items-center gap-1 text-[17px] text-[#0062CC]"><ChevronLeft className="h-5 w-5" /> Cancel</Link>
        <button data-testid="merge-swap" onClick={() => nav(`/customers/${other}/merge?with=${id}`, { replace: true })} className="inline-flex min-h-[44px] items-center gap-1.5 text-[15px] text-[#0062CC]"><ArrowLeftRight className="h-4 w-4" /> Swap which one is kept</button>
      </div>
      <div>
        <h1 className="text-[34px] font-bold leading-tight text-black">Merge customers</h1>
        <p className="mt-1 text-[15px] text-[#6D6D72]">Tap the value to keep for each field. Notes are combined. Sent and approved estimates keep the details they were sent with.</p>
      </div>
      <div className="grid grid-cols-2 gap-2 px-4 text-[13px] text-[#6D6D72]" data-testid="merge-headers">
        <p><span className="font-semibold text-black">Keep: {a.name}</span><br />{plural(a.projects, "project")} · {plural(a.estimates, "estimate")}</p>
        <p><span className="font-semibold text-black">Merge in: {b.name}</span><br />{plural(b.projects, "project")} · {plural(b.estimates, "estimate")} · {plural(b.invoices, "invoice")}</p>
      </div>
      {showroomMismatch && <p data-testid="merge-showroom-note" className={`rounded-[12px] px-4 py-3 text-[15px] ${pv.can_merge ? "bg-[#FF9500]/10 text-[#8A4B00]" : "bg-[#FF3B30]/10 text-[#D70015]"}`}>
        {pv.can_merge ? "These customers are in different showrooms. Pick the showroom and a sales rep from it below." : pv.block_reason}</p>}
      <div className="overflow-hidden rounded-[12px] bg-[#F9F9FB]">
        {pv.fields.map((f, i) => <Choice key={f.key} f={f} i={i} pick={choose[f.key]} setPick={(s) => setChoose({ ...choose, [f.key]: s })} />)}
      </div>
      {pv.notes && <div className="space-y-1.5"><p className="px-4 text-[13px] uppercase text-[#6D6D72]">Combined notes</p>
        <p data-testid="merge-notes" className="whitespace-pre-wrap rounded-[12px] bg-white px-4 py-3 text-[15px] text-[#3C3C43]">{pv.notes}</p></div>}
      <button data-testid="merge-confirm-button" disabled={busy || !pv.can_merge} onClick={run}
        className="flex min-h-[50px] w-full items-center justify-center rounded-[12px] bg-[#0071E3] text-[17px] font-semibold text-white disabled:opacity-40">Merge into {a.name}</button>
    </div>
  );
}
